import type { CatalogVendeurPublic } from "../catalog/catalogTenant";
import { AppBaseUrl } from "./http";

export type CatalogShareMeta = {
  title: string;
  description: string;
  image: string;
  url: string;
};

function toAbsoluteUrl(url: string): string {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (typeof window === "undefined") return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${window.location.origin}${path}`;
}

export function buildCatalogShareMeta(
  vendeur: Pick<
    CatalogVendeurPublic,
    "nom_boutique" | "nom_de_la_boutique" | "avatar" | "catalog_url"
  >,
  pageUrl?: string
): CatalogShareMeta {
  const shopName =
    vendeur.nom_boutique ||
    vendeur.nom_de_la_boutique ||
    "cette boutique";

  const base = AppBaseUrl.replace(/\/$/, "");
  const rawUrl =
    pageUrl ||
    vendeur.catalog_url ||
    (typeof window !== "undefined" ? window.location.href : base);
  const fallbackImage = `${base}/logo.png`;

  return {
    title: `Catalogue ${shopName}`,
    description: `Découvrez le catalogue ${shopName} sur EasyMarket. Parcourez nos produits et commandez en ligne.`,
    image: toAbsoluteUrl(vendeur.avatar || fallbackImage),
    url: rawUrl.startsWith("http") ? rawUrl : toAbsoluteUrl(rawUrl),
  };
}

function upsertMeta(
  selector: string,
  attributes: Record<string, string>
): void {
  if (typeof document === "undefined") return;

  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => {
      if (key !== "content") el!.setAttribute(key, value);
    });
    document.head.appendChild(el);
  }
  if (attributes.content != null) {
    el.setAttribute("content", attributes.content);
  }
}

function upsertLink(rel: string, href: string): void {
  if (typeof document === "undefined" || !href) return;

  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Met à jour title + Open Graph / Twitter (navigateur, après chargement React). */
export function applyCatalogShareMeta(meta: CatalogShareMeta): void {
  if (typeof document === "undefined") return;

  document.title = meta.title;

  upsertMeta('meta[name="description"]', {
    name: "description",
    content: meta.description,
  });
  upsertMeta('meta[property="og:title"]', {
    property: "og:title",
    content: meta.title,
  });
  upsertMeta('meta[property="og:description"]', {
    property: "og:description",
    content: meta.description,
  });
  upsertMeta('meta[property="og:image"]', {
    property: "og:image",
    content: meta.image,
  });
  upsertMeta('meta[property="og:url"]', {
    property: "og:url",
    content: meta.url,
  });
  upsertMeta('meta[property="og:type"]', {
    property: "og:type",
    content: "website",
  });
  upsertMeta('meta[name="twitter:title"]', {
    name: "twitter:title",
    content: meta.title,
  });
  upsertMeta('meta[name="twitter:description"]', {
    name: "twitter:description",
    content: meta.description,
  });
  upsertMeta('meta[name="twitter:image"]', {
    name: "twitter:image",
    content: meta.image,
  });
  upsertMeta('meta[name="twitter:card"]', {
    name: "twitter:card",
    content: "summary_large_image",
  });
}

export function applyCatalogShareMetaFromVendeur(
  vendeur: Pick<
    CatalogVendeurPublic,
    "nom_boutique" | "nom_de_la_boutique" | "avatar" | "catalog_url"
  >
): void {
  const meta = buildCatalogShareMeta(vendeur);
  applyCatalogShareMeta(meta);
  if (vendeur.avatar) {
    upsertLink("icon", meta.image);
    upsertLink("apple-touch-icon", meta.image);
  }
}

export function resetCatalogShareMeta(): void {
  applyCatalogShareMeta({
    title: "EasyMarket Catalogue",
    description:
      "Découvrez les catalogues boutiques sur EasyMarket et commandez en ligne.",
    image: toAbsoluteUrl(`${AppBaseUrl.replace(/\/$/, "")}/logo.png`),
    url: AppBaseUrl.replace(/\/$/, ""),
  });
}
